import { useState, useSyncExternalStore } from 'react';
import { useMessage, useThread, type MessageState, type ThreadState } from '@assistant-ui/react';

type ThreadText = { id: string; role: string; content: ReadonlyArray<{ type: string; text?: string }> };

/** The line the composer puts first when a voice is picked (useEmbeddedIvyRuntime). */
const VOICE_LINE = /^Use voice: (.+)$/m;

/** The voice the first of your messages after `messageId` sent, or null. */
export function sentVoiceAfter(messages: readonly ThreadText[], messageId: string | null): string | null {
  const at = messageId ? messages.findIndex((m) => m.id === messageId) : -1;
  if (at < 0) return null;
  for (const m of messages.slice(at + 1)) {
    if (m.role !== 'user') continue;
    const voice = VOICE_LINE.exec(m.content.find((p) => p.type === 'text')?.text ?? '')?.[1]?.trim();
    if (voice) return voice;
  }
  return null;
}

const picks = new Map<string, string>();
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

/**
 * A voice card's answer, held outside the card so a rebuilt card still shows it: the voice a later message of yours
 * sent, else the pick made on this message's card this visit, else (no message around the card) the card's own pick.
 * Picking also hands the voice to the chat, as before (ivy-voice-selected).
 */
export function useVoiceAnswer(recommended: string): [string | null, (voice: string) => void] {
  const messageId = useMessage({ optional: true, selector: (m: MessageState) => m.id }) ?? null;
  const sent = useThread({ optional: true, selector: (t: ThreadState) => sentVoiceAfter(t.messages as readonly ThreadText[], messageId) }) ?? null;
  const key = messageId && `${messageId}|${recommended}`;
  const kept = useSyncExternalStore(subscribe, () => (key ? picks.get(key) ?? null : null));
  const [own, setOwn] = useState<string | null>(null);

  const pick = (voice: string) => {
    if (key) {
      picks.set(key, voice);
      listeners.forEach((l) => l());
    } else setOwn(voice);
    window.dispatchEvent(new CustomEvent('ivy-voice-selected', { detail: { voice } }));
  };
  return [sent ?? kept ?? own, pick];
}
